import { logger } from '@adornis/base/logging.js';
import '@adornis/buchungsheld-components/bh-breadcrumb.js';
import { BuchungsheldLitElement } from '@adornis/buchungsheld-components/BuchungsheldLitElement.js';
import { Institution } from '@adornis/buchungsheld-db/entities/institution.js';
import {
  InstitutionMailTemplate,
  MailTemplateType,
  subscribeMailTemplatesForInstitution,
} from '@adornis/buchungsheld-db/entities/mail-templates.js';
import '@adornis/buchungsheld-theme/components/tr-loader.js';
import '@adornis/buchungsheld-theme/components/tr-mails-template-editor.js';
import '@adornis/buchungsheld-theme/fonts/fonts.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-section.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import '@adornis/file-utils/client/x-image.js';
import '@adornis/mails/client/x-mails-template-editor.js';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { filter, map, of, switchMap } from 'rxjs';
import { BHFormController } from '../../../../controller/BHFormController.js';

/**
 * @element bh-page-admin-einrichtung-settings-mails
 */
@customElement('bh-page-admin-einrichtung-settings-mails')
export class BHPageAdminEinrichtungSettingsMails extends BuchungsheldLitElement {
  @property({ attribute: 'institution-id' }) institutionID = new RXController<string | null>(this, null);

  @state() private readonly _mailTemplates = new RXController(
    this,
    this.institutionID.observable.pipe(
      filter(Boolean),
      switchMap(institutionID => Institution.getByID<Institution>(institutionID)(Institution.allFields)),
      filter(Boolean),
      switchMap(institution =>
        subscribeMailTemplatesForInstitution(institution._id)(InstitutionMailTemplate.allFields),
      ),
      map(mailTemplates => {
        // sort templates by name
        return mailTemplates.sort((a, b) => {
          return a.name.localeCompare(b.name);
        });
      }),
    ),
  );
  @state() private readonly _institutionController = new BHFormController(
    this,
    this.institutionID.observable.pipe(
      switchMap(institutionID =>
        !institutionID ? of(null) : Institution.getByID<Institution>(institutionID)(Institution.allFields),
      ),
    ),
    {
      success: async institution => {
        try {
          await XDialog.waitFor(institution.save());
        } catch (err) {
          logger.error({ err }, 'Es gab ein Problem beim aktualisieren des Mail Banner Bildes');
          void XDialog.alert(this._translation.translate('settings', 'err_mail_banner'));
        }
      },
    },
  );

  override render() {
    return html`
      <x-flex space="xl">
        <bh-breadcrumb
          .items=${[
            {
              content: this._translation.translate('settings', 'name'),
              href: `/admin/institution-details/${this.institutionID.value}/settings`,
            },
            { content: 'E-Mail-Templates', active: true },
          ]}
        ></bh-breadcrumb>
        <x-flex space="lg">
          <x-text ${css({ maxWidth: this.sizes.maxReadableWidth })}>
            ${this._translation.translate('settings', 'override_mail_msg')}
          </x-text>
          ${this._mailTemplates.isLoading
            ? html` <x-flex flex center crossaxis-center> <tr-loader></tr-loader> </x-flex> `
            : html`
                <tr-mails-template-editor
                  .getMailTemplates=${() =>
                    this._mailTemplates.value?.filter(template => {
                      // TODO: maybe use new scope property to handle this?
                      switch (template.emailType) {
                        case MailTemplateType.INFO_NEWSLETTER:
                          return false;
                        default:
                          return true;
                      }
                    }) ?? []}
                  overriding
                ></tr-mails-template-editor>
              `}
          <tr-card ${css({ width: '100%' })}>
            <x-flex space="sm">
              <tr-text> ${this._translation.translate('settings', 'config_mail_banner_image')} </tr-text>
              <tr-text tone="subtle"> ${this._translation.translate('settings', 'upload_mail_banner_image')} </tr-text>
            </x-flex>
            <x-image
              ${this._institutionController.field('emailBannerImageID')}
              mode="contain"
              ${css({ width: '100%', height: '250px' })}
            ></x-image>
            <tr-button
              icon="save"
              ${css({ alignSelf: 'flex-end' })}
              @click=${(e: MouseEvent) => this._institutionController.validate({ mouseEvent: e })}
            >
              Banner Bild speichern
            </tr-button>
          </tr-card>
        </x-flex>
      </x-flex>
    `;
  }
}
